import { Decimal } from 'decimal.js';
/**
 * Aritmética monetária. Nunca usar `number` para dinheiro em cálculos: tudo
 * passa por Decimal e só no fim se converte para string com 2 casas.
 *
 * Arredondamento: half-up (ROUND_HALF_UP), a 2 casas — a regra documentada
 * na Memória Descritiva e a que um contabilista espera ao conferir à mão.
 */
export const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export const ZERO = new D(0);
export function dec(v) {
    if (v === null || v === undefined || v === '')
        return ZERO;
    return v instanceof D ? v : new D(v);
}
/** Arredonda a 2 casas, half-up. */
export function round2(v) {
    return dec(v).toDecimalPlaces(2, D.ROUND_HALF_UP);
}
/**
 * Casas decimais com que cada valor é gravado na BD: quantidades, preços e
 * custos em numeric(18,4); percentagens e valores em meticais com 2 casas.
 */
export const SCALE = { quantity: 4, price: 4, pct: 2, money: 2, rate: 6 };
/**
 * Arredonda (half-up) à escala com que o valor vai ser gravado. Os valores
 * entram no cálculo JÁ arredondados: calcular com 1,00005 e gravar 1,0001
 * emitia facturas cujos totais não recalculavam a partir das linhas gravadas
 * (RN-CAL-011, RN-SEG-004; decisão P-17: arredondar, não recusar).
 */
export function toScale(v, places) {
    return dec(v).toDecimalPlaces(places, D.ROUND_HALF_UP).toFixed();
}
/** Representação canónica para gravar na BD e assinar: "1234.50". */
export function money(v) {
    return round2(v).toFixed(2);
}
/** Formato pt-MZ para apresentação: "1.234,50". */
export function formatMoney(v, decimals = 2) {
    const s = dec(v).toFixed(decimals);
    const neg = s.startsWith('-');
    const [int = '0', frac] = (neg ? s.slice(1) : s).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (neg ? '-' : '') + grouped + (frac !== undefined ? ',' + frac : '');
}
/**
 * Interpreta um valor monetário digitado por uma pessoa. Portado de
 * fact_parse_money() do sistema Ribeiro, com a mesma regra:
 *
 *  - o separador mais à DIREITA é o decimal, o outro é de milhares
 *    ("1.234,56" e "1,234.56" valem o mesmo);
 *  - um único tipo de separador: é de milhares SÓ quando aparece mais do que
 *    uma vez ("1.234.567") ou é seguido de exactamente três dígitos ("1.000"
 *    é mil, como se escreve em meticais). Qualquer outro número de casas é
 *    decimal: "5813.66", "99,5", e "1000.0000" — o formato canónico em que a
 *    própria BD devolve preços com 4 casas. (A primeira versão tratava 4 casas
 *    como milhares; o teste ponta-a-ponta apanhou um preço de 1000 a virar
 *    10 000 000 ao converter uma pró-forma em factura.)
 *
 * Existe por causa de um erro real: a versão antiga apagava sempre o ponto e
 * "5813.66" virava 581366, sem erro nenhum num preço de artigo.
 *
 * Decisões P-18/P-29 (Fase 7 do plano de testes):
 *  - a mesma regra vale para valores, quantidades e percentagens ("1.500" é mil
 *    e quinhentos em qualquer campo);
 *  - com a parte inteira "0" ou vazia, o separador é sempre decimal: ninguém
 *    escreve milhares a começar por zero ("0,125" é 0,125 e não 125);
 *  - texto com letras que não sejam a moeda (MT, MZN) não é um número: "1e3"
 *    valia 13 sem aviso; agora é recusado.
 *
 * Devolve string decimal canónica ("5813.66") ou null se não houver número.
 */
export function parseMoney(input) {
    if (input === null || input === undefined)
        return null;
    if (typeof input === 'number')
        return Number.isFinite(input) ? new D(input).toString() : null;
    let s = input.trim();
    if (s === '')
        return null;
    // Letras só as da moeda; qualquer outra (ex.: "1e3", "12abc") torna o texto inválido.
    if (/\p{L}/u.test(s.replace(/mzn|mt/giu, '')))
        return null;
    const negative = /^\s*-/.test(s) || /^\(.*\)$/.test(s);
    // Fica só o que pode ser número: dígitos e os dois separadores. Sai a moeda,
    // espaços, e o espaço não-separável (U+00A0/U+202F) do toLocaleString.
    s = s.replace(/[^\d.,]/gu, '');
    if (s === '' || !/\d/.test(s))
        return null;
    const dot = s.lastIndexOf('.');
    const comma = s.lastIndexOf(',');
    if (dot !== -1 && comma !== -1) {
        s = comma > dot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
    }
    else if (comma !== -1) {
        s = singleSeparator(s, ',');
    }
    else if (dot !== -1) {
        s = singleSeparator(s, '.');
    }
    if (!/^\d*\.?\d*$/.test(s) || s === '.')
        return null;
    const out = new D(s.startsWith('.') ? '0' + s : s);
    return (negative ? out.negated() : out).toString();
}
function singleSeparator(s, sep) {
    const pos = s.lastIndexOf(sep);
    const decimals = s.length - pos - 1;
    const count = s.split(sep).length - 1;
    const intPart = s.slice(0, pos);
    if (count === 1 && decimals > 0 && (decimals !== 3 || /^0*$/.test(intPart)))
        return s.replace(sep, '.');
    return s.split(sep).join('');
}
//# sourceMappingURL=money.js.map